if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.primaryThumbRail = this.elements.thumbnails || null;
        this.productHandle = this.dataset.productHandle || this.getAttribute('data-product-handle') || '';
        this.groupingOption = (this.dataset.mediaGroupingOption || 'Color').split(',').map((s) => s.trim()).filter(Boolean);
        this.groupToIds = null;
        if (!this.elements.viewer) return;
        this.onSlideChanged = this.onSlideChanged.bind(this);
        this.onThumbClick = this.onThumbClick.bind(this);
        this.queueFilter = this.queueFilter.bind(this);
        this.computeGroups().then(() => {
          if (this.primaryThumbRail) {
            this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged);
            this.addEventListeners();
            if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
              this.removeListSemantic();
            }
            this.filterByCurrent();
          }
        });
      }
      async computeGroups() {
        try {
          if (!this.productHandle) return;
          const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
          const url = root + 'products/' + this.productHandle + '.js';
          const res = await fetch(url);
          if (!res.ok) return;
          const product = await res.json();
          const opt = product.options.find((o) => this.groupingOption.includes(o.name)) || null;
          if (!opt) return;
          const colorValues = opt.values || [];
          const mapStart = {};
          colorValues.forEach((val) => {
            const v = product.variants.find((va) => va.options[opt.position - 1] === val);
            if (v && v.featured_media) mapStart[val] = v.featured_media.position;
          });
          const sorted = Object.entries(mapStart).sort((a, b) => a[1] - b[1]);
          const ranges = {};
          sorted.forEach((entry, i) => {
            const name = entry[0];
            const start = entry[1];
            const end = sorted[i + 1] ? sorted[i + 1][1] : null;
            ranges[name] = end ? [start, end] : [start];
          });
          this.groupToIds = {};
          Object.entries(ranges).forEach(([color, range]) => {
            const start = range[0];
            const end = range[1] || product.media.length + 1;
            const ids = product.media
              .filter((m, idx) => {
                const pos = idx + 1;
                return pos >= start && pos < end;
              })
              .map((m) => m.id);
            this.groupToIds[color] = ids;
          });
          const sectionId = this.id.replace('MediaGallery-', '');
          Object.entries(this.groupToIds).forEach(([color, ids]) => {
            ids.forEach((id) => {
              const sel = '[data-media-id="' + sectionId + '-' + id + '"], [data-target="' + sectionId + '-' + id + '"]';
              this.querySelectorAll('li' + sel).forEach((li) => {
                li.setAttribute('data-media-group', color);
              });
            });
          });
        } catch (e) {}
      }
      addEventListeners() {
        this.primaryThumbRail.querySelectorAll('[data-target] > button').forEach((btn) => {
          btn.addEventListener('click', this.onThumbClick);
        });
        const infoRoot = this.closest('product-info') || document;
        infoRoot.addEventListener('change', this.queueFilter);
        document.addEventListener('variant:change', (e) => {
          const target = e && e.target;
          if (!target) return;
          const root = this.closest('product-info') || document;
          if (root.contains(target)) this.queueFilter();
        });
        window.addEventListener('popstate', this.queueFilter);
      }
      queueFilter() {
        this.filterByCurrent();
      }
      getSelectedColor() {
        if (!this.groupToIds) return null;
        const keys = Object.keys(this.groupToIds);
        if (!keys.length) return null;
        const root = this.closest('product-info') || document;
        const radios = Array.from(root.querySelectorAll('fieldset input[type="radio"]:checked')).map((i) => i.value);
        const selects = Array.from(root.querySelectorAll('select')).map((s) => {
          if (s.multiple) return Array.from(s.selectedOptions).map((o) => o.value);
          return s.value || (s.selectedOptions[0] && s.selectedOptions[0].value) || '';
        });
        const flat = selects.flat ? selects.flat() : ([]).concat(...selects);
        const selected = radios.concat(flat).map((v) => v && v.toString().trim()).filter(Boolean);
        let match = keys.find((k) => selected.includes(k));
        if (match) return match;
        const lcSelected = selected.map((v) => v.toLowerCase());
        match = keys.find((k) => lcSelected.includes(k.toLowerCase()));
        return match || keys[0];
      }
      filterByCurrent() {
        if (!this.groupToIds) return;
        const color = this.getSelectedColor();
        if (!color) return;
        this.filterThumbnails(color);
      }
      filterThumbnails(color) {
        if (!this.primaryThumbRail) return;
        const ids = this.groupToIds[color] || [];
        const thumbLis = this.primaryThumbRail.querySelectorAll('li[data-target], li[data-media-group]');
        thumbLis.forEach((li) => {
          const mid = (li.getAttribute('data-target') || '').split('-').pop();
          const visible = ids.includes(Number(mid)) && li.getAttribute('data-media-group') === color;
          li.style.display = visible ? '' : 'none';
        });
        if (this.primaryThumbRail.resetPages) this.primaryThumbRail.resetPages();
      }
      onThumbClick(e) {
        const btn = e.currentTarget;
        const li = btn.closest('[data-target]');
        if (!li || !this.primaryThumbRail.contains(li)) return;
        e.preventDefault();
        e.stopPropagation();
        const mediaId = li.getAttribute('data-target');
        this.setActiveMedia(mediaId, false);
      }
      onSlideChanged(event) {
        const rail = this.primaryThumbRail;
        if (!rail) return;
        const el = event.detail && event.detail.currentElement ? event.detail.currentElement : null;
        const mediaId = el && el.dataset ? el.dataset.mediaId : '';
        if (!mediaId) return;
        const thumb = rail.querySelector('[data-target="' + mediaId + '"]');
        this.setActiveThumbnail(thumb);
      }
      setActiveMedia(mediaId, prepend) {
        const candidates = this.elements.viewer.querySelectorAll('[data-media-id="' + mediaId + '"]');
        let activeMedia =
          Array.from(candidates).find((el) => el.offsetParent !== null) ||
          candidates[0] ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
          el.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) activeMedia.parentElement.prepend(activeMedia);
          if (this.primaryThumbRail) {
            const at = this.primaryThumbRail.querySelector('[data-target="' + mediaId + '"]');
            if (at && at.parentElement.firstChild !== at) at.parentElement.prepend(at);
          }
          if (this.elements.viewer.slider && this.elements.viewer.resetPages) this.elements.viewer.resetPages();
        }
        if (!this.mql.matches) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        this.playActiveMedia(activeMedia);
        if (!this.primaryThumbRail) return;
        const activeThumb = this.primaryThumbRail.querySelector('[data-target="' + mediaId + '"]');
        this.setActiveThumbnail(activeThumb);
        const pos = activeThumb && activeThumb.dataset ? activeThumb.dataset.mediaPosition : '';
        this.announceLiveRegion(activeMedia, pos);
      }
      setActiveThumbnail(thumbnail) {
        if (!this.primaryThumbRail || !thumbnail) return;
        this.primaryThumbRail.querySelectorAll('button').forEach((el) => el.removeAttribute('aria-current'));
        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', true);
        if (this.primaryThumbRail.isSlideVisible && this.primaryThumbRail.isSlideVisible(thumbnail, 10)) return;
        if (this.primaryThumbRail.slider) this.primaryThumbRail.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }
      playActiveMedia(activeItem) {
        if (window.pauseAllMedia) window.pauseAllMedia();
        const deferred = activeItem.querySelector('.deferred-media');
        if (deferred && deferred.loadContent) deferred.loadContent(false);
      }
      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems) {
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }
    }
  );
}

